import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Play, Sparkles, Rocket } from 'lucide-react';
import { LandingHeader } from '../components/layout/landing-header';

export default function VideoDemo() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
      <LandingHeader showBackButton={false} currentPage="video" />

      {/* Hero Section with added padding for header */}
      <div className="container mx-auto px-4 pt-24 pb-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Play className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              See PageMind in Action
            </h1>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Watch how PageMind transforms your website into an intelligent, self-optimizing platform that understands and engages your visitors
          </p>
        </div>

        {/* Video Section */}
        <div className="max-w-6xl mx-auto mb-16">
          <Card className="overflow-hidden shadow-2xl">
            <CardContent className="p-0">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/Fjf7xtvzfHQ?rel=0&showinfo=0&modestbranding=1"
                  title="PageMind Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-5xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">AI-Powered Intelligence</h3>
            <p className="text-gray-600 dark:text-gray-400">
              20+ specialized AI agents working together to understand and optimize your site
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Rocket className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">5-Minute Setup</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Simple onboarding process gets you up and running in minutes, not days
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArrowRight className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Instant Results</h3>
            <p className="text-gray-600 dark:text-gray-400">
              See immediate improvements in engagement and conversion rates
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Website?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of businesses already using PageMind to deliver exceptional user experiences
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://onboarding.pagemind.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button
                size="lg"
                className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a
              href="/signup"
              className="inline-block"
            >
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-purple-600 font-semibold px-8 py-6 text-lg transition-all"
              >
                Sign Up Now
              </Button>
            </a>
          </div>
          <p className="text-purple-100 mt-6 text-sm">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Have questions? Check out our <a href="https://www.pagemind.ai/features" className="text-purple-600 hover:underline">features page</a> or <a href="https://www.pagemind.ai/contact" className="text-purple-600 hover:underline">contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
}